/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import _ from 'lodash';

function isSame(prev, next) {
  return prev.url === next.url &&
          _.isEqual(prev.headers, next.headers) &&
          _.isEqual(prev.query, next.query);
}

/**
 *
 * Connects a wrapped component to requests state and provides request handlers via props. Also dispatches
 * requests based on page load and stale properties.
 *
 * Internal connect function. Should be in redux connect component and around wrapped component
 *
 * Expects to be passed dispatch function, handleInitialization function and requestState object
 * through properties.
 *
 * Connect style callback connect(options)(Component)
 *
 * @param  {Function} (mapPropsToRequests) Selector function that returns a map of requests.
 *                                        Requsts contain method, url, onResponse and onError callback promises
 *                                        and onLoad boolean flat.
 *
 * @return {Component}                    Wrapped component
 */
export function withHOC(WrappedComponent) {
  const wrappedComponentName = WrappedComponent.displayName ||
                               WrappedComponent.name ||
                               'Component';

  return class RequestConnect extends React.Component {
    static displayName= `withRequests(${wrappedComponentName})`;

    /**
     * Run before component is mounted.
     *
     * Construct initialization action
     *
     * Perform an requests marked with onLoad = true
     *
     */
    UNSAFE_componentWillMount() {
      const { requests } = this.props;
      _.map(requests, (request) => {
        if (request.onLoad) {
          request.handleRequest();
        } else if (request.handleInitialization) {
          request.handleInitialization();
        }
      });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      const prevRequests = this.props.requests;
      const nextRequests = nextProps.requests;
      _.map(nextRequests, (nextRequest, key) => {
        const prevRequest = prevRequests[key];  
        if (!prevRequest || !isSame(prevRequest, nextRequest)) {
          if (nextRequest.onLoad) {
            nextRequest.handleRequest();
          } else if (nextRequest.handleInitialization) {
            nextRequest.handleInitialization();
          }
        }
      });
    }

    /**
     * Resets local requests on unmount
     */
    componentWillUnmount() {
      const { requests } = this.props;
      _.map(requests, (request) => {
        if (request.onLoad) request.handleReset();
      });
    }


    render() {
      // Catch components props and don't pass them
      const { requests, requestAliases, ...rest } = this.props;
      // if (this.state.initializing.length > 0) return null;
      return <WrappedComponent {...rest} {...requestAliases} requests={requests} />;
    }
  };
}
